<template>
  <draggable
    v-model="mutableValue"
    v-bind="dragOptions"
    handle=".dashboard-widget-action-handle"
    :disabled="!draggable"
    class="fts-dashboard-widget"
    :style="cssVariable"
    @start="dragging = true"
    @end="dragging = false"
    @change="$emit('update')"
  >
    <div
      v-for="x in filteredWidgets"
      :key="x.id"
      class="dashboard-widget-wrapper"
      :class="[
        getGridClass(x),
        {'dashboard-widget-wrapper--design': draggable}
      ]"
      @mouseenter="hoverWidgetId = x.id"
      @mouseleave="hoverWidgetId = null"
    >
      <!-- shortcut -->
      <shortcut
        :modules="modules"
        :item="x"
        :filters="filters"
        @entry:create="evt => $emit('entry:create', evt)"
        @route:listing="evt => $emit('route:listing', evt)"
        @configure="callback => configure(x, callback)"
        @delete="$emit('delete', x)"
        v-if="x.widgetType === 'shortcut'"
      />
      <!-- chart -->
      <chart
        :modules="modules"
        :states="states"
        :users="users"
        :item="x"
        :filters="filters"
        :readonly="readonly"
        @route:listing="evt => $emit('route:listing', evt)"
        @segment-clicked="segmentClicked"
        @configure="callback => configure(x, callback)"
        @delete="$emit('delete', x)"
        v-else-if="x.widgetType === 'chart'"
      />
      <!-- predefined -->
      <activity-stream-card
        :modules="modules"
        :states="states"
        :widget="x"
        :widget-data="dashboardData"
        :loading="loading"
        :initial="initial"
        :activity-everyone-allowed="activityEveryoneAllowed"
        @entry:view="evt => $emit('entry:view', evt)"
        v-else-if="x.type === 'activityStreamCard'"
      />
      <calendar-card
        :modules="modules"
        :states="states"
        :widget="x"
        :widget-data="dashboardData"
        :loading="loading"
        :initial="initial"
        @entry:view="evt => $emit('entry:view', evt)"
        v-else-if="x.type === 'calendarCard'"
      />
      <initial-card
        :modules="modules"
        :states="states"
        :widget="x"
        :widget-data="dashboardData"
        :loading="loading"
        :initial="initial"
        @entry:view="evt => $emit('entry:view', evt)"
        v-else-if="x.type === 'initialCard'"
      />
      <mention-card
        :modules="modules"
        :states="states"
        :widget="x"
        :widget-data="dashboardData"
        :loading="loading"
        :initial="initial"
        @entry:view="evt => $emit('entry:view', evt)"
        v-else-if="x.type === 'mentionCard'"
      />
      <task-card
        :modules="modules"
        :states="states"
        :widget="x"
        :widget-data="dashboardData"
        :loading="loading"
        :initial="initial"
        @entry:view="evt => $emit('entry:view', evt)"
        v-else-if="x.type === 'taskCard'"
      />
      <!-- line break -->
      <div v-else-if="x.widgetType === 'lineBreak'" />
      <!-- design -->
      <template v-if="draggable && isConfigurable">
        <!-- design:overlay -->
        <div class="dashboard-widget-design-overlay" v-if="dragging" />
        <!-- design:handle -->
        <div class="dashboard-widget-action-container" v-show="hoverWidgetId == x.id || activeWidgetId == x.id">
          <fts-dropdown-select
            append-to-body
            :offset="[0, 8]"
            placement="right"
            prevent-overflow-alt-axis
            prevent-overflow-tether-off
            overlay
            @show="activeWidgetId=x.id"
            @hide="activeWidgetId=null"
            v-if="x.widgetType == 'predefined'"
          >
            <fts-tippy slot="toggle">
              <font-awesome-icon :icon="['fas', 'grip-vertical']" class="dashboard-widget-action-handle" />
              <template #content>
                <div class="tippy-hint">
                  <span class="tippy-hint-highlight">Drag</span> to move
                </div>
                <div class="tippy-hint">
                  <span class="tippy-hint-highlight">Click</span> to open menu
                </div>
              </template>
            </fts-tippy>
            <template #menu="{ select }">
              <div class="dropdown-option" @click="select();deleteWidgetConfirmation(x)">
                <div class="dropdown-option-icon">
                  <font-awesome-icon :icon="['far', 'trash-can']" fixed-width />
                </div>
                <div>Remove Card</div>
              </div>

              <div class="dropdown-option" @click="select();resize(x, { row: 6 })" v-if="x.row != 6">
                <div class="dropdown-option-icon">
                  <font-awesome-icon :icon="['fas', 'up-right-and-down-left-from-center']" fixed-width />
                </div>
                <div>Maximize Card</div>
              </div>

              <div class="dropdown-option" @click="select();resize(x, { row: 3 })" v-else>
                <div class="dropdown-option-icon">
                  <font-awesome-icon :icon="['fas', 'down-left-and-up-right-to-center']" fixed-width />
                </div>
                <div>Minimize Card</div>
              </div>
            </template>
          </fts-dropdown-select>

          <fts-tippy v-else>
            <font-awesome-icon :icon="['fas', 'grip-vertical']" class="dashboard-widget-action-handle" />
            <template #content>
              <div class="tippy-hint">
                <span class="tippy-hint-highlight">Drag</span> to move
              </div>
            </template>
          </fts-tippy>
        </div>
      </template>
    </div>
  </draggable>  
</template>

<script>
import draggable from 'vuedraggable';

import { authorizationStore } from '../store/authorizationStore';
import commons from '../commons/commons';

import Shortcut from './widgets/Shortcut';
import Chart from './widgets/Chart';
import ActivityStreamCard from './widgets/ActivityStreamCard';
import CalendarCard from './widgets/CalendarCard';
import InitialCard from './widgets/InitialCard';
import MentionCard from './widgets/MentionCard';
import TaskCard from './widgets/TaskCard';
import FTSBtn from '../elements/FTSBtn';
import FTSDropdownSelect from '../elements/FTSDropdownSelect';
import FTSTippy from '../elements/FTSTippy';

export default {
  components: {
    draggable,
    "shortcut": Shortcut,
    "chart": Chart,
    "activity-stream-card": ActivityStreamCard,
    "calendar-card": CalendarCard,
    "initial-card": InitialCard,
    "mention-card": MentionCard,
    "task-card": TaskCard,
    "fts-btn": FTSBtn,
    "fts-dropdown-select": FTSDropdownSelect,
    "fts-tippy": FTSTippy
  },
  props: {
    modules: Array,
    states: Array,
    users: Array,
    widgetData: Array,
    dashboardData: Object, /* legacy */
    loading: Boolean,
    initial: Boolean,
    /* SS03547: passed through to the activity stream card (see FTSDashboard). */
    activityEveryoneAllowed: {
      type: Boolean,
      default: true
    },
    draggable: Boolean,
    readonly: Boolean,
    filters: Object,
    selectedModuleId: [String, Number]
  },
  model: {
    prop: "widgetData",
    event: "updateWidgetData",
  },
  data: () => ({
    cssVariable: null,
    dragOptions: commons.dragOptions,
    dragging: false,
    hoverWidgetId: null,
    activeWidgetId: null
  }),
  methods: {
    segmentClicked(payload) {
      this.$emit('segment-clicked', payload)
    },
    getGridClass(item) {
      if (item.widgetType === 'lineBreak') {
        return 'dashboard-widget-col-full';
      }

      let gridClass = [];
      /* col */
      if (
        item.col === 3
      ) {
        gridClass.push('dashboard-widget-col-3');
      } else if (
        item.col === 2 ||
        (item.widgetType === 'chart' && ['bar', 
          'stackedBar', 'stackedBarHorizontal',
          'barHorizontal', 'line', 'doughnut', 'pie', 'polarArea', 'radar', 'content'].indexOf(item.type) > -1)
      ) {
        gridClass.push('dashboard-widget-col-2');
      } else {
        gridClass.push('dashboard-widget-col');
      }
      /* row */
      if (item.row === 6) {
        gridClass.push('dashboard-widget-row-6');
      } else if (item.row === 5) {
        gridClass.push('dashboard-widget-row-5');
      } else if (item.row === 4) {
        gridClass.push('dashboard-widget-row-4');
      } else if (
        item.row === 3 ||
        (item.widgetType === 'chart' && ['bar', 
          'stackedBar', 'stackedBarHorizontal',
          'barHorizontal', 'line', 'doughnut', 'pie', 'polarArea', 'radar', 'content'].indexOf(item.type) > -1)
      ) {
        gridClass.push('dashboard-widget-row-3');
      } else if (
        item.row === 2
      ) {
        gridClass.push('dashboard-widget-row-2');
      } else {
        gridClass.push('dashboard-widget-row');
      }

      return [...new Set(gridClass)].join(' ');
    },
    configure(item, callback) {
      this.$emit("configure", {
        formData: item,
        callback: callback,
      });
    },
    updateColumn() {
      const calculateColumn = (width, col) => {
        if (((this.$el.clientWidth - ((col - 1) * 16)) / col) >= width) {
          return col;
        }
        if (col === 1) {
          return col;
        }
        return calculateColumn(width, col -1);
      }  
      const col3 = () => {
        const space = calculateColumn(540, 6);
        if (space >= 2) return 3;
        else return 6;
      }
      const col2 = () => {
        const space = calculateColumn(360, 6);
        if (space >= 3) return 2;
        else if (space == 2) return 3;
        else return 6; 
      }
      const col = () => {
        const space = calculateColumn(180, 6);
        if (space >= 6) return 1;
        else if (space >= 3) return 2; 
        else return 3;
      }
      this.cssVariable = {
        '--widget-col-full': 'span 6',
        '--widget-col-3': `span ${col3()}`,
        '--widget-col-2': `span ${col2()}`,
        '--widget-col': `span ${col()}`
      }
    },
    resize(item, { row, col } = {}) {
      let widgets = [...this.mutableValue];
      const widgetIndex = widgets.findIndex(x => x.id == item.id);
      if (widgetIndex > -1) {
        if (item.widgetType === 'predefined' && row) {
          this.$set(widgets, widgetIndex, { ...item, row: row });
          this.mutableValue = widgets;
          this.$emit("update");
        }
      }
    },
    async deleteWidgetConfirmation(item) {
      let payload = { type: 'delete' };
      if (item.widgetType === 'predefined') {
        payload = {
          text: `Are you sure you want to remove ${item.name}?`
        }
      }
      const res = await this.$confirmation.show(payload);
      if (res === true) {
        this.$emit("delete", item);
      }
    }
  },
  computed: {
    filteredWidgets() {
      return this.widgetData.filter(x => !this.selectedModuleId || x.moduleId == this.selectedModuleId)
    },
    isConfigurable() {
      return !this.readonly && authorizationStore.getters.isAdminUser;
    },
    mutableValue: {
      get() {
        return this.widgetData;
      },
      set(val) {
        this.$emit("updateWidgetData", val);
      }
    }
  },
  mounted() {
    this.updateColumn();
    window.eventBus.$on("dashboard:resize", this.updateColumn);
  },
  beforeDestroy() {
    window.eventBus.$off("dashboard:resize", this.updateColumn);
  }
}
</script>

<style lang="scss" scoped>
.fts-dashboard-widget {
  width: 100%;
  display: grid;
  // gap: 1rem;
  column-gap: 1rem; /* temp sol */
  grid-template-columns: repeat(6, minmax(0, 1fr));
  .dashboard-widget-wrapper { /* temp sol */
    &:not(.dashboard-widget-col-full) {
      margin-bottom: 1rem;
    }
    &.dashboard-widget-wrapper--design {
      position: relative;
      .dashboard-widget-design-overlay {
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
      }
      .dashboard-widget-action-container {
        position: absolute;
        top: 0.75rem;
        left: -0.5rem;
        height: auto;
        padding: 0.375rem 0.25rem;
        border-radius: 0.25rem;
        box-shadow: var(--fts-elevation-2);
        background-color: var(--fts-popover-alt-background);
        font-size: 0.75rem;
        color: var(--fts-default);
        user-select: none;
      }
      .dashboard-widget-action-handle {
        cursor: grab;
      }
    }
  }
  .dashboard-widget-col {
    grid-column: var(--widget-col)
  }
  .dashboard-widget-col-2 {
    grid-column: var(--widget-col-2)
  }
  .dashboard-widget-col-3 {
    grid-column: var(--widget-col-3);
  }
  .dashboard-widget-col-full {
    grid-column: var(--widget-col-full);
  }
  .dashboard-widget-row {
    height: 128px;
    max-height: 128px;
    > * {
      height: 100%;
    }
  }
  .dashboard-widget-row-2 {
    grid-row: span 2;
    height: 272px;
    max-height: 272px;
    > * {
      height: 100%;
    }
  }
  .dashboard-widget-row-3 {
    grid-row: span 3;
    height: 416px;
    max-height: 416px;
    > * {
      height: 100%;
    }
  }
  .dashboard-widget-row-4 {
    grid-row: span 4;
    height: 560px;
    max-height: 560px;
    > * {
      height: 100%;
    }
  }
  .dashboard-widget-row-5 {
    grid-row: span 5;
    height: 704px;
    max-height: 704px;
    > * {
      height: 100%;
    }
  }
  .dashboard-widget-row-6 {
    grid-row: span 6;
    height: 848px;
    max-height: 848px;
    > * {
      height: 100%;
    }
  }
}
</style>